import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, JSX } from "react";

import { cn } from "@/lib/cn";

/**
 * Stage, lead and variant tags are grey (brief 08, section 4). Colour is kept for a state
 * that needs action: `success` (paid, won), `warning` (due soon), `danger` (overdue,
 * lost, failed), `info`. `ai` is the machine's mark and nothing else.
 */
export const badgeVariants = cva(
  "inline-flex h-5 shrink-0 items-center gap-1 rounded-small px-2 text-xs font-medium whitespace-nowrap tabular-nums [&_svg]:size-3",
  {
    variants: {
      tone: {
        neutral: "bg-surface-muted text-ink-muted",
        success: "bg-success-soft text-success-ink",
        warning: "bg-warning-soft text-warning-ink",
        danger: "bg-danger-soft text-danger-ink",
        info: "bg-info-soft text-info-ink",
        ai: "bg-ai text-ai-ink",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

/** A tag or status pill. Text is the only content; an icon may lead it. */
export function Badge({ tone, className, ...rest }: BadgeProps): JSX.Element {
  return <span className={cn(badgeVariants({ tone }), className)} {...rest} />;
}

/** Same primitive, named for the place it is used: a tag on a row, not a status. */
export const Pill = Badge;
